import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import HtmlHead from 'client/components/htmlHead';
import { Tracking } from 'web-core/src/tracking';
import { fetchProfessionsIfNeeded, saveProfessions } from 'client/actions/tax/professions';
import { fetchCategories, fetchStaticCategoriesIfNeeded } from 'client/actions/tax/expenses';
import Translate from 'client/components/translate';
import Card from 'client/components/card/card';
import LinkButton from 'client/components/button/linkButton';
import BottomBarContainer from 'client/components/bottomBarContainer';
import ExpenseHero from 'client/components/tax/expenseHero';
import { ProfessionShape, ExpenseShape, MileageDeductionShape } from 'client/constants/tax/shapes';
import ExpenseRow from 'client/components/tax/expenseRow';
import '../../styles/report.scss';

const propTypes = {
  user: PropTypes.shape({
    isLoggedIn: PropTypes.bool,
  }).isRequired,
  expenses: PropTypes.arrayOf(ExpenseShape).isRequired,
  professions: PropTypes.arrayOf(ProfessionShape).isRequired,
  mileageTypes: PropTypes.objectOf(MileageDeductionShape).isRequired,
  saveProfessions: PropTypes.func.isRequired,
  staticCategories: PropTypes.shape({
    nonDeductible: PropTypes.shape({
      categories: PropTypes.arrayOf(ExpenseShape),
      title: PropTypes.string.isRequired,
    }),
    nonDeductibleMileage: PropTypes.shape({
      categories: PropTypes.arrayOf(ExpenseShape),
      title: PropTypes.string.isRequired,
    }),
  }).isRequired,
  location: PropTypes.shape({
    query: PropTypes.shape({
      selected: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    }).isRequired,
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  params: PropTypes.shape({
    jobs: PropTypes.arrayOf(PropTypes.string),
  }),
  fetchCategories: PropTypes.func.isRequired,
};

const defaultProps = {
  staticCategories: {
    nonDeductible: {},
    nonDeductibleMileage: {},
  },
  params: {},
};

const mapStateToProps = (state, ownProps) => {
  const jobs = Array.isArray(ownProps.params.jobs)
    ? ownProps.params.jobs.map(p => p) // filter out undefined values
    : ['uber-driver'];
  const professions = state.professions.content.filter(p => jobs.indexOf(p.seoSlug) > -1);
  const professionKey = professions
    .filter(p => jobs.indexOf(p.seoSlug) > -1)
    .map(p => p.id)
    .sort()
    .join('-');

  return {
    user: state.user,
    professions: state.professions.content.filter(p => jobs.indexOf(p.seoSlug) > -1),
    expenses: state.expenses.categories.filter(
      c => state.expenses.displayedByJob[professionKey]?.indexOf(c.id) > -1,
    ),
    staticCategories: state.expenses.staticCategories,
    mileageTypes: state.professions.mileageTypes,
  };
};

const hasMileageTypeIds = profession =>
  profession.mileageTypeIds && profession.mileageTypeIds.length > 0;

const ctaButtonProps = {
  className: 'cta-button auto',
};
const trackAction = (type) => {
  Tracking.track('Deduction Guide: Action', { Type: type });
};

class ReportPage extends Component {
  // if we're hitting this directly, grab the data on the server so it can be rendered for SEO
  static fetchData({ params, dispatch }) {
    return dispatch(fetchProfessionsIfNeeded()).then((response) => {
      const jobs = Array.isArray(params.jobs) ? params.jobs.filter(p => p) : ['uber-driver'];
      const professionIds = response.professions
        .filter(p => jobs.indexOf(p.seoSlug) > -1)
        .map(p => p.id);

      return Promise.all([
        dispatch(fetchCategories(professionIds)),
        dispatch(fetchStaticCategoriesIfNeeded()),
      ]);
    });
  }

  UNSAFE_componentWillMount() {
    // If redirecting from account creation, we need to save professions for the
    // new user account. Dummy search string "?s" appended to URL on redirect,
    // which we then remove
    if (this.props.location.search === '?s') {
      this.props.router.replace(this.props.location.pathname);
      const professionIds = this.props.professions.map(p => p.id);
      this.props.saveProfessions(professionIds);
    }

    if (!this.props.expenses.length) {
      const jobs = Array.isArray(this.props.params.jobs)
        ? this.props.params.jobs.filter(p => p)
        : ['uber-driver'];
      const professionIds = this.props.professions
        .filter(p => jobs.indexOf(p.seoSlug) > -1)
        .map(p => p.id);
      this.props.fetchCategories(professionIds);
    }

    this.bottomBarContent = (
      <div className="center-col flex-row">
        <h4 className="flex-child-expand pad-x hide-for-small-only">
          <Translate path="taxes.deductionGuide.footer" />
        </h4>
        {this.props.user && this.props.user.isLoggedIn ? (
          <LinkButton
            href="/my/taxes"
            {...ctaButtonProps}
            onClick={() => trackAction('view-dashboard')}>
            <Translate path="taxes.deductionGuide.cta.loggedIn" />
          </LinkButton>
        ) : (
          <LinkButton
            href="/account/create?source=deduction-guide&redirect=my/taxes"
            {...ctaButtonProps}
            onClick={() => trackAction('main-signup')}>
            <Translate path="taxes.deductionGuide.cta.loggedOut" />
          </LinkButton>
        )}
      </div>
    );
  }

  componentDidMount() {
    Tracking.track('Deduction Guide: View', {
      Jobs: this.props.professions.map(p => p.name).join(', '),
      'Logged In': this.props.user.isLoggedIn,
    });
  }

  render() {
    const { user, expenses, staticCategories, professions, mileageTypes } = this.props;
    const editUrlParams = professions
      .map(profession => `selected=${profession.seoSlug}`)
      .join('&');
    const editUrl = `/deductions/select-job?${editUrlParams}`;
    const hasMileageDeductions = professions.filter(hasMileageTypeIds).length > 0;
    const professionTitles = professions.map(p => p.professionTitle.toLowerCase());
    const professionsCount = professionTitles.length;
    const professionTitleString =
      professionsCount > 1
        ? `${professionTitles.slice(0, professionsCount - 1).join(', ')} and ${
          professionTitles[professionsCount - 1]
        }`
        : professionTitles[0];
    return (
      <div className="report-page">
        <HtmlHead
          title={`Stride Health - Tax Deduction Guide - ${professions
            .map(p => p.displayName)
            .join(' - ')}`}
          description={
            professionsCount > 1
              ? professions.map(p => p.displayName).join(' ')
              : professions[0].introDescription
          } />
        <BottomBarContainer barContent={this.bottomBarContent} bottomBarClassName="bottom-bar-bg">
          <div>
            <ExpenseHero className="pad-y-2" professions={professions} />
            <div className="center-col">
              {hasMileageDeductions && (
                <Card highlight="green" className="expense-card">
                  <div>
                    <h4 className="section-title">
                      <Translate path="taxes.deductionGuide.report.mileage.title" />
                    </h4>
                    <div className="mileage-sections">
                      {professions.filter(hasMileageTypeIds).map(item => (
                        <div className="pad-top mileage-section" key={item.id}>
                          {professions.length > 1 && (
                            <h4 className="margin-bottom">{item.displayName}</h4>
                          )}
                          {item.mileageTypeIds.map(mileageTypeId => (
                            <ExpenseRow
                              className="mileage-deduction"
                              key={mileageTypeId}
                              header={mileageTypes[mileageTypeId].title}
                              description={mileageTypes[mileageTypeId].description}
                              illustration={mileageTypes[mileageTypeId].illustration} />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              <Card highlight="green" className="expense-card">
                <div>
                  <h4 className="section-title">
                    <Translate path="taxes.deductionGuide.report.expenses.title" />
                  </h4>
                  {expenses.map(item => (
                    <ExpenseRow
                      key={item.id}
                      className="expense"
                      header={item.displayName}
                      description={item.helpText}
                      illustration={item.illustration} />
                  ))}
                </div>
              </Card>

              {professions.length < 2 && (
                <Card highlight="green" className="expense-card">
                  <div>
                    <h4 className="section-title">
                      <Translate path="taxes.deductionGuide.report.otherExpenses.title" />
                    </h4>
                    <div className="text-center pad-bottom">
                      <Link
                        to={editUrl}
                        className="circle big-plus"
                        onClick={() => trackAction('add-jobs')}>
                        <strong>+</strong>
                      </Link>
                      <p className="margin">
                        <Translate path="taxes.deductionGuide.report.otherExpenses.description" />
                      </p>
                      <LinkButton
                        to={editUrl}
                        onClick={() => trackAction('add-jobs')}
                        {...ctaButtonProps}>
                        <Translate path="taxes.deductionGuide.report.otherExpenses.cta" />
                      </LinkButton>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            <div className="brand-gradient margin-2">
              <div className="center-col" style={{ padding: '3.5rem 1.5rem' }}>
                <h2>
                  <Translate
                    path="taxes.deductionGuide.report.callout.title"
                    using={{ professions: professionTitleString }} />
                </h2>
                <p className="margin">
                  <Translate path="taxes.deductionGuide.report.callout.description" />
                </p>
                {user && user.isLoggedIn ? (
                  <LinkButton
                    href="/my/taxes"
                    {...ctaButtonProps}
                    onClick={() => trackAction('view-dashboard')}>
                    <Translate path="taxes.deductionGuide.cta.loggedIn" />
                  </LinkButton>
                ) : (
                  <LinkButton
                    href="/account/create?redirect=/my/taxes&source=deduction-guide"
                    {...ctaButtonProps}
                    onClick={() => trackAction('main-signup')}>
                    <Translate path="taxes.deductionGuide.cta.loggedOut" />
                  </LinkButton>
                )}
              </div>
            </div>

            <div className="center-col">
              {staticCategories.nonDeductible &&
                Array.isArray(staticCategories.nonDeductible.categories) && (
                  <Card highlight="red" className="expense-card">
                    <div>
                      <h4 className="section-title">{staticCategories.nonDeductible.title}</h4>
                      {staticCategories.nonDeductible.categories.map(item => (
                        <ExpenseRow
                          key={item.name}
                          className="expense"
                          header={item.displayName}
                          description={item.helpText}
                          illustration={item.illustration} />
                      ))}
                    </div>
                  </Card>
                )}

              {staticCategories.nonDeductibleMileage &&
                Array.isArray(staticCategories.nonDeductibleMileage.categories) && (
                  <Card highlight="orange" className="expense-card">
                    <div>
                      <h4 className="section-title">
                        {staticCategories.nonDeductibleMileage.title}
                      </h4>
                      {staticCategories.nonDeductibleMileage.categories.map(item => (
                        <ExpenseRow
                          key={item.name}
                          className="expense"
                          header={item.displayName}
                          description={item.helpText}
                          illustration={item.illustration} />
                      ))}
                    </div>
                  </Card>
                )}
            </div>
          </div>
        </BottomBarContainer>
      </div>
    );
  }
}

ReportPage.propTypes = propTypes;
ReportPage.defaultProps = defaultProps;

export default connect(mapStateToProps, {
  fetchCategories,
  fetchStaticCategoriesIfNeeded,
  saveProfessions,
})(ReportPage);
